// frontend/src/components/legal/privacyPolicy.jsx
import React from 'react';
import { getPublicPageDefinition } from '../public/publicSiteRegistry.mjs';
import {
  LegalCallout,
  LegalContactBlock,
  LegalList,
  LegalPageShell,
  LegalSection,
} from './legalPageShell';

const PAGE = getPublicPageDefinition('/privacy-policy');
const EFFECTIVE_DATE = 'November 3, 2026';

const TABLE_OF_CONTENTS = [
  ['privacy-information-collected', '1. Information we collect'],
  ['privacy-use', '2. How we use information'],
  ['privacy-google-drive', '3. Google Drive and Google user data'],
  ['privacy-google-sharing', '4. Google Drive receipt sharing'],
  ['privacy-sharing', '5. How we share information'],
  ['privacy-retention', '6. Data retention and deletion'],
  ['privacy-security', '7. Security'],
  ['privacy-cookies', '8. Cookies, sessions, and analytics'],
  ['privacy-choices', '9. Your choices and privacy rights'],
  ['privacy-third-parties', '10. Third-party services'],
  ['privacy-children', "11. Children's privacy"],
  ['privacy-changes', '12. Changes to this Policy'],
  ['privacy-contact', '13. Contact us'],
].map(([id, label]) => ({ id, label }));

export default function PrivacyPolicy() {
  return (
    <LegalPageShell
      page={PAGE}
      effectiveDate={EFFECTIVE_DATE}
      tableOfContents={TABLE_OF_CONTENTS}
      summary={
        <>
          <p>
            TaskerSys is a property operations platform with optional field services. It is
            operated by Copala Inc. (&quot;TaskerSys,&quot; &quot;Copala,&quot; &quot;we,&quot;
            &quot;us,&quot; or &quot;our&quot;). This Privacy Policy explains how we collect,
            use, disclose, store, and protect information when you use taskersys.com, the
            TaskerSys platform, related browser experiences, and TaskerSys field-service
            workflows.
          </p>
          <p>
            This Policy also explains our use of Google user data when a TaskerSys user
            connects Google Drive for invoice receipt storage. Google Drive access is optional
            and is used only for the user-facing receipt-storage and receipt-sharing features
            described below.
          </p>
        </>
      }
      closingNote={
        <p>
          This Privacy Policy describes TaskerSys&apos;s current practices. It should be reviewed
          when product features, data practices, service providers, or legal requirements
          materially change.
        </p>
      }
    >
      <LegalSection id="privacy-information-collected" title="1. Information we collect">
        <p>Depending on how you use TaskerSys, we may collect or process:</p>
        <LegalList>
          <li>
            <strong>Account and profile information</strong>, such as name, username, email
            address, telephone number, business/company information, role, profile details,
            service-provider information, and addresses you provide.
          </li>
          <li>
            <strong>Property and operations information</strong>, such as building, unit,
            service-request, ticket, turnover, maintenance, project, assignment, scheduling,
            access, scope, approval, vendor, subcontractor, technician, and closeout information.
          </li>
          <li>
            <strong>User-provided content</strong>, including comments, messages, photos,
            videos, documents, receipts, invoices, bids, quotes, reports, checklists, and other
            files submitted through the platform.
          </li>
          <li>
            <strong>Financial and business records</strong> that users enter into TaskerSys,
            such as invoice amounts, receipt amounts, payment references, budgets,
            payable/receivable information, and accounting-related records.
          </li>
          <li>
            <strong>Field-work information</strong>, which may include schedules, timekeeping
            information, work progress, completion evidence, and, where enabled for a field
            workflow, device/location information used to support attendance, dispatch, or
            job-site verification.
          </li>
          <li>
            <strong>Technical and usage information</strong>, such as browser/device
            information, IP address, session information, logs, page activity, and analytics
            used to operate, secure, and improve the service.
          </li>
          <li>
            <strong>Communications</strong>, such as support requests, contact-form
            submissions, subscription requests, and other communications with us.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection id="privacy-use" title="2. How we use information">
        <p>We use information to:</p>
        <LegalList>
          <li>provide, operate, maintain, and improve TaskerSys;</li>
          <li>create and manage accounts, permissions, roles, workspaces, and profiles;</li>
          <li>
            coordinate work orders, maintenance, turnovers, projects, vendors, dispatchers,
            taskers, subcontractors, technicians, approvals, and reporting;
          </li>
          <li>
            provide field services, respond to requests, prepare quotes or proposals, schedule
            work, document progress, and complete closeout reporting;
          </li>
          <li>
            create and manage invoices, receipts, supporting documents, financial records, and
            related operational workflows;
          </li>
          <li>authenticate users and protect accounts and the platform from misuse;</li>
          <li>provide customer support and respond to questions;</li>
          <li>send requested product, platform, or field-service updates;</li>
          <li>comply with legal obligations and enforce our agreements; and</li>
          <li>
            understand product usage and improve performance, reliability, and user experience.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection id="privacy-google-drive" title="3. Google Drive and Google user data">
        <p>
          TaskerSys offers an optional Google Drive integration for invoice receipt storage.
          When a user chooses to connect Google Drive, TaskerSys requests only the Google
          permissions needed for that feature, including basic account identification and the
          Google Drive <code>drive.file</code> permission.
        </p>
        <p>With this integration, TaskerSys may access or process:</p>
        <LegalList>
          <li>the Google account email address and account identifier used to establish the connection;</li>
          <li>
            file and folder identifiers, names, URLs, metadata, and permissions for Google Drive
            files or folders the user selects through Google Picker or that TaskerSys creates or
            uses through the receipt-storage feature;
          </li>
          <li>
            invoice receipt files the user directs TaskerSys to upload or synchronize to the
            selected Google Drive folder; and
          </li>
          <li>
            OAuth authorization credentials needed to maintain the connection, such as a refresh
            token stored in encrypted form and short-lived access tokens used to perform authorized
            Drive operations.
          </li>
        </LegalList>
        <p>
          TaskerSys does <strong>not</strong> request broad access to all files in a user&apos;s
          Google Drive. The <code>drive.file</code> permission is intended to limit access to files
          and folders the user chooses to use with TaskerSys or that TaskerSys creates for the
          feature.
        </p>
        <p>
          We use Google user data only to provide the user-facing Google Drive receipt features,
          including connecting the user&apos;s account, allowing the user to select a receipt root
          folder, creating invoice/date folders, uploading or synchronizing invoice receipts, and
          creating receipt-sharing links when the user enables that functionality.
        </p>
        <LegalCallout>
          <strong>Google API Limited Use statement.</strong> TaskerSys&apos;s use and transfer to
          any other app of information received from Google APIs will adhere to the Google API
          Services User Data Policy, including the Limited Use requirements.
        </LegalCallout>
        <p>
          We do not sell Google user data, use it for targeted or personalized advertising, use it
          to determine creditworthiness or lending eligibility, or use it to train or improve
          generalized artificial intelligence or machine learning models.
        </p>
        <p>
          We do not permit human access to Google user data except when the user has expressly
          asked us to help with specific data, when access is necessary for security or abuse
          investigation, when required by law, or when otherwise permitted under Google&apos;s
          applicable user-data policies.
        </p>
      </LegalSection>

      <LegalSection id="privacy-google-sharing" title="4. Google Drive receipt sharing">
        <p>
          When a user selects the option to upload invoice receipts to Google Drive, TaskerSys may
          create an invoice-specific <strong>Receipts</strong> folder beneath the user&apos;s
          selected receipt root folder. If the user enables the invoice receipt-link feature,
          TaskerSys may apply a read-only &quot;anyone with the link&quot; permission to the
          invoice-specific receipt folder and place that link on the invoice.
        </p>
        <p>
          The user&apos;s selected root folder is not intentionally made public by this feature.
          However, anyone who obtains an invoice-specific receipt link may be able to view or
          download the files in that shared folder. Users should not enable public receipt links
          for documents they do not want invoice recipients or other link holders to access.
        </p>
        <p>
          Disconnecting Google Drive from TaskerSys stops TaskerSys from using the stored Google
          authorization for future operations. Disconnecting does not automatically delete files
          already uploaded to the user&apos;s Google Drive and may not remove sharing permissions
          previously created. Users can review, remove, or change Google Drive permissions directly
          in Google Drive.
        </p>
      </LegalSection>

      <LegalSection id="privacy-sharing" title="5. How we share information">
        <p>
          We may share information only as reasonably necessary to operate TaskerSys and provide
          requested services, including:
        </p>
        <LegalList>
          <li>
            with users, organizations, clients, managers, service providers, subcontractors,
            technicians, or other participants whose access is necessary for the user&apos;s
            selected TaskerSys workflow;
          </li>
          <li>
            with hosting, cloud-storage, email, analytics, security, and other service providers
            that process information on our behalf;
          </li>
          <li>
            with invoice recipients when a user intentionally provides or enables a receipt-sharing
            link or other shared document;
          </li>
          <li>
            when required to comply with law, legal process, or valid governmental request, or to
            protect rights, safety, security, and the integrity of the service; and
          </li>
          <li>
            in connection with a merger, financing, acquisition, reorganization, or sale of assets,
            subject to applicable law and required notices or consent.
          </li>
        </LegalList>
        <p>We do not sell personal information or Google user data to data brokers or advertising networks.</p>
      </LegalSection>

      <LegalSection id="privacy-retention" title="6. Data retention and deletion">
        <p>
          We retain information for as long as reasonably necessary to provide the service,
          maintain business and financial records, comply with legal obligations, resolve disputes,
          enforce agreements, and protect the platform. Retention periods can vary by data type and
          business context.
        </p>
        <p>
          Google OAuth refresh tokens are retained only while the Google Drive connection is
          maintained or as otherwise necessary to complete an authorized operation. A user can
          disconnect Google Drive from the TaskerSys profile, which removes the stored TaskerSys
          connection record and causes TaskerSys to stop using that authorization. Google account
          access may also be revoked through the user&apos;s Google Account settings.
        </p>
        <p>
          Files uploaded to a user&apos;s own Google Drive remain under that user&apos;s Google Drive
          account and are not automatically deleted merely because the TaskerSys connection is
          disconnected. Users remain responsible for deleting or changing sharing permissions on
          those files and folders when desired.
        </p>
        <p>
          To request deletion of other personal information maintained by TaskerSys, contact us
          using the information below. We may retain information where required by law or where a
          legitimate recordkeeping obligation applies.
        </p>
      </LegalSection>

      <LegalSection id="privacy-security" title="7. Security">
        <p>
          We use reasonable administrative, technical, and organizational safeguards designed to
          protect information. For Google Drive integrations, TaskerSys does not store the
          user&apos;s Google password. Long-lived Google authorization credentials used by the
          receipt-storage feature are stored in encrypted form on the server, and access tokens are
          intended to be short-lived.
        </p>
        <p>No method of transmission or storage is completely secure, and we cannot guarantee absolute security.</p>
      </LegalSection>

      <LegalSection id="privacy-cookies" title="8. Cookies, sessions, and analytics">
        <p>
          TaskerSys may use cookies, session identifiers, local storage, and similar technologies
          to keep users signed in, remember preferences, protect the service, operate application
          features, and understand usage. Browser or device settings may allow users to limit some
          of these technologies, although doing so may affect application functionality.
        </p>
      </LegalSection>

      <LegalSection id="privacy-choices" title="9. Your choices and privacy rights">
        <p>
          Depending on applicable law and your relationship with TaskerSys, you may have rights to
          request access, correction, deletion, or a copy of certain personal information, or to
          object to or restrict certain processing. Requests may require identity verification.
        </p>
        <p>
          Google Drive users can disconnect TaskerSys through their TaskerSys profile and can also
          revoke Google access through their Google Account. Users can manage Drive files and
          sharing permissions directly in Google Drive.
        </p>
        <p>
          You may opt out of promotional or update emails by following any unsubscribe instructions
          provided, replying with an opt-out request, or contacting us. We may still send
          transactional, security, account, or service-related communications when necessary.
        </p>
      </LegalSection>

      <LegalSection id="privacy-third-parties" title="10. Third-party services">
        <p>
          TaskerSys may interoperate with third-party services, including Google Drive and other
          services selected by users. Third-party services are governed by their own terms and
          privacy practices. TaskerSys is not responsible for a third party&apos;s independent
          handling of information after a user directs information to that third party.
        </p>
      </LegalSection>

      <LegalSection id="privacy-children" title="11. Children's privacy">
        <p>
          TaskerSys is designed for business, property-operations, and field-service use and is not
          directed to children under 13. We do not knowingly collect personal information from
          children under 13 through the service.
        </p>
      </LegalSection>

      <LegalSection id="privacy-changes" title="12. Changes to this Policy">
        <p>
          We may update this Privacy Policy from time to time. When we make material changes, we
          will update the effective date above and may provide additional notice where appropriate.
        </p>
      </LegalSection>

      <LegalSection id="privacy-contact" title="13. Contact us">
        <LegalContactBlock />
      </LegalSection>
    </LegalPageShell>
  );
}

